import React, { useEffect, useRef, useState } from 'react'
import Table from '../../../shared/table'
import { Box, StackView, Link } from '@planningcenter/tapestry-react'
import { Code, PageHeading, Text } from '../../../shared/typography'
import useWindowWidth from '../../../utils/useWindowWidth'

const TermsTable = () => {
  const box = useRef()
  const [definitionWidth, setDefinitionWidth] = useState(0)
  const windowWidth = useWindowWidth()

  useEffect(() => {
    if (!box.current) {
      return
    }

    let boxWidth = box.current.getBoundingClientRect().width
    let termWidth = box.current
      .querySelector('[role=columnheader]:first-child')
      ?.getBoundingClientRect().width

    box.current
      .querySelectorAll('[role=rowgroup]:nth-child(2) > [role=row]')
      .forEach(r => (r.style.minWidth = 'unset'))
    setDefinitionWidth(boxWidth - termWidth - 64)
  }, [windowWidth, setDefinitionWidth])

  const columns = [
    {
      header: 'Term',
      cell: row => (
        <Text>
          <b>{row.term}</b>
        </Text>
      ),
    },
    {
      header: 'Definition',
      cell: row => row.definition(),
      width: definitionWidth,
      css: { whiteSpace: 'wrap' },
    },
  ]

  const data = [
    {
      term: 'Add-on',
      definition: () => (
        <Text>
          This term is <em>almost</em> synonymous with an OAuth Application, but
          specifically denotes an OAuth Application that also has UI Extensions
          rendered by Planning Center.
        </Text>
      ),
    },
    {
      term: 'Add-on Scopes',
      definition: () => (
        <Text>
          These are the OAuth scopes that the add-on will request on behalf of
          the user in order to get an API token that does what your add-on
          needs. (These are separate and may be different than the scopes needed
          by your OAuth Application proper.) These add-on scopes are configured
          in the <Code>config.yaml</Code> file described later in this document.
        </Text>
      ),
    },
    {
      term: 'Component',
      definition: () => (
        <Text>
          A{' '}
          <Link
            to="https://en.wikipedia.org/wiki/React_(software)#Components"
            external
          >
            React Component
          </Link>{' '}
          is a single class or function that controls a portion of the user
          interface for your add-on. Larger components can and often are made of
          smaller components and layered in a way to make the composition
          easy-to-understand and allow reuse of common bits of UI.
        </Text>
      ),
    },
    {
      term: 'Insertion Point',
      definition: () => (
        <Text>
          This is a point in a specific Planning Center product where a UI
          Extension will get rendered. It is up to each Planning Center product
          team to implement these points in places where add-on authors wish to
          present their own UI.
        </Text>
      ),
    },
    {
      term: 'Integrator',
      definition: () => (
        <Text>
          This is you! If you are a company or individual building an add-on for
          Planning Center, then you are an "Integrator" with us. We'll use this
          term in some of the later sections and even code to differentiate your
          stuff vs ours.
        </Text>
      ),
    },
    {
      term: 'OAuth Application',
      definition: () => (
        <Text>
          The application configured at{' '}
          <Link
            to="https://api.planningcenteronline.com/oauth/applications"
            external
          >
            https://api.planningcenteronline.com/oauth/applications
          </Link>{' '}
          listing your app's name, description, URL, callback URLs, etc. This
          allows your application code to make requests to the Planning Center
          API.
        </Text>
      ),
    },
    {
      term: 'OAuth Server',
      definition: () => (
        <Text>
          The server that our system uses to talk to your API. It must conform
          to the OAuth2 specification and allow Planning Center to obtain a
          token for use by your add-on JavaScript. This allows Planning Center
          to make requests to your (the Integrator's) API on behalf of your
          add-on.
        </Text>
      ),
    },
    {
      term: 'UI Extension',
      definition: () => (
        <Text>
          The React component (written in JavaScript) that is rendered by
          Planning Center and shown in the UI. Each add-on has one or more UI
          Extensions.
        </Text>
      ),
    },
    {
      term: 'Web Worker',
      definition: () => (
        <Text>
          A{' '}
          <Link to="https://en.wikipedia.org/wiki/Web_worker" external>
            Web Worker
          </Link>{' '}
          is a separate thread of JavaScript execution in the web browser,
          separate from the main thread that most other scripts execute inside.
          Planning Center add-ons execute in a Web Worker and thus have no
          access to the DOM directly.
        </Text>
      ),
    },
  ]

  return (
    <Box innerRef={box}>
      <Table columns={columns} data={data} />
    </Box>
  )
}

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Add-ons Terminology</PageHeading>

      <Box>
        <TermsTable />
      </Box>
    </StackView>
  )
}

export default page
